import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'

interface Options {
  enabled: boolean
}

/**
 * Fetches the server-authoritative `priceInPoints` conversion for a given price.
 * Gated by `enabled` (e.g. the calendar burn toggle) — does not depend on the
 * global `hasSelectedBurnToggle` flag. Internal helper; consumers use
 * `useCalendarBurnDiscountedPrice` below.
 */
function useCalendarBurnPriceInPoints(
  price: number,
  loyaltyProductType: App.LoyaltyProductType | undefined,
  mx: string | undefined,
  { enabled }: Options,
): number | undefined {
  const dispatch = useAppDispatch()
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')

  const request = useMemo(() => {
    if (!enabled || !loyaltyProductType || price <= 0) return undefined
    return { price, loyaltyProductType, mx }
  }, [enabled, loyaltyProductType, price, mx])

  const batchKey = request ? buildBurnKey(request, customerId) : undefined

  const priceInPoints = useAppSelector(state => (
    batchKey ? state.loyalty.pointsBurnBatchCalculations?.[batchKey]?.data?.priceInPoints : undefined
  ))

  useEffect(() => {
    if (!request) return
    dispatch(submitLoyaltyPointsBurnCalculationRequest(request))
  }, [dispatch, request, customerId])

  return priceInPoints
}

interface BurnDiscountedPriceOptions {
  /** Total cash price to discount (hotel or hotel+flight package). */
  total: number
  /** Hotel-only cash price (for the hotel burn calc). */
  hotelPrice: number
  /** Flight-only cash price, if this is a bundled hotel+flight display. */
  flightPrice?: number
  loyaltyProductType: App.LoyaltyProductType | undefined
  /** User's points applied (typically their full balance for the calendar preview). */
  userPoints: number
  enabled: boolean
}

/**
 * Returns the cash price after applying `userPoints` worth of points, using
 * server-authoritative `priceInPoints` conversions. Handles both hotel-only and
 * split hotel+flight burn. Falls back to `total` when burn is disabled or the
 * conversion isn't available yet.
 */
export function useCalendarBurnDiscountedPrice({
  total,
  hotelPrice,
  flightPrice = 0,
  loyaltyProductType,
  userPoints,
  enabled,
}: BurnDiscountedPriceOptions): number {
  const splitBurn = flightPrice > 0
  const hotelInPoints = useCalendarBurnPriceInPoints(hotelPrice, loyaltyProductType, undefined, { enabled })
  const flightInPoints = useCalendarBurnPriceInPoints(flightPrice, 'int_economy', undefined, { enabled: enabled && splitBurn })

  if (!enabled) return total

  const combined = splitBurn
    ? (hotelInPoints !== undefined && flightInPoints !== undefined ? hotelInPoints + flightInPoints : undefined)
    : hotelInPoints

  if (!combined || combined <= 0) return total

  return Math.ceil(Math.max(0, total - (userPoints * total) / combined))
}
